import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Sun, Moon, Menu, X, ArrowRight } from 'lucide-react';

const LINKS = [
    { label: 'Mission', href: '#mission' },
    { label: 'How it works', href: '#how-it-works' },
    { label: 'Road Safety', href: '#road-safety' },
    { label: 'Impact', href: '#impact' },
];

/**
 * Public marketing navigation — sticky, glass, one primary CTA ("Report a Violation").
 * Report routes to the dashboard when signed in, otherwise to /login.
 */
export default function PublicNav({ onReport }) {
    const { isAuthenticated } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    return (
        <header className="sticky top-0 z-50 glass border-b">
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-label="Primary">
                <div className="flex items-center justify-between h-16">
                    <Link to="/" className="flex items-center gap-2 group" aria-label="TrafficWatch home">
                        <img src="/logo.png" alt="" className="h-9 w-auto rounded-md transition-transform group-hover:scale-105" />
                        <span className="font-heading font-bold text-lg tracking-tight">TrafficWatch</span>
                    </Link>

                    <div className="hidden md:flex items-center gap-1">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                className="px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            >
                                {l.label}
                            </a>
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={toggleTheme} className="h-9 w-9" aria-label="Toggle theme">
                            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                        </Button>
                        {!isAuthenticated && (
                            <Button variant="ghost" className="hidden sm:inline-flex" onClick={() => navigate('/login')}>
                                Sign in
                            </Button>
                        )}
                        <Button className="hidden sm:inline-flex" onClick={onReport}>
                            Report a Violation
                            <ArrowRight className="w-4 h-4 ml-1.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden h-9 w-9"
                            onClick={() => setOpen((v) => !v)}
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            aria-expanded={open}
                        >
                            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>

                {open && (
                    <div className="md:hidden py-3 border-t animate-fade-in">
                        <div className="flex flex-col gap-1">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    className="px-3 py-3 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                >
                                    {l.label}
                                </a>
                            ))}
                            {!isAuthenticated && (
                                <Button variant="outline" className="mt-2" onClick={() => { setOpen(false); navigate('/login'); }}>
                                    Sign in
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
}
